// THE LINES LEFT OUT OF THE REPORT, and the way back.
//
// Owner, 2026-10-11: "add bulk ignore for multiple checked items, so they
// don't get included in the report". Ignored is not deleted: the lines are
// out of every total and export above, and listed here - who left them out,
// when, why - so a total can always be explained and any of them restored.
import React, { useState } from "react";
import { Button, Card, Popconfirm, Table, Tooltip, message } from "antd";
import { restoreIgnoredItems } from "api/billableHours";

const T = { muted: "#667085" };
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const hrs = (n) => `${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })} h`;
const day = (v) => (v ? new Date(v).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "–");

export default function IgnoredLines({ ignored, canEdit = false, onChanged }) {
  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState([]);
  const [busy, setBusy] = useState(false);
  if (!ignored || !ignored.items) return null;
  const rows = (ignored.rows || []).map((r, i) => ({ ...r, _k: `${r.ignoredId}:${i}` }));
  const withCost = "cost" in ignored;

  const restore = (ids) => {
    setBusy(true);
    restoreIgnoredItems([...new Set(ids)])
      .then((r) => { message.success(`${r.restored} item${r.restored === 1 ? "" : "s"} back in the report`); setPicked([]); if (onChanged) onChanged(); })
      .catch((e) => message.error((e && (e.err || e.message)) || "Could not bring them back"))
      .finally(() => setBusy(false));
  };

  const columns = [
    { title: "Contractor", dataIndex: "companyName", key: "c", render: (v, r) => v || r.companyId || "(no company recorded)" },
    { title: "Project", dataIndex: "projectTitle", key: "p", render: (v, r) => v || <span style={{ color: T.muted }}>{r.projectId}</span> },
    {
      title: "Item", key: "item",
      render: (_, r) => {
        const it = r.item || {};
        return <span><span style={{ color: T.muted }}>{it.kind}</span> <b>{it.label || r.ref}</b>{it.title ? <span> · {it.title}</span> : null}</span>;
      },
    },
    { title: "Hours", dataIndex: "hours", key: "h", align: "right", render: (v) => (v == null ? <span style={{ color: T.muted }}>not entered</span> : hrs(v)) },
    ...(withCost ? [{ title: "Cost", dataIndex: "cost", key: "m", align: "right", render: (v) => (v == null ? <span style={{ color: T.muted }}>–</span> : money(v)) }] : []),
    {
      title: "Ignored", key: "by",
      render: (_, r) => (
        <span>
          {r.ignoredBy || "—"}, {day(r.ignoredAt)}
          {r.ignoredReason ? <div style={{ color: T.muted, fontSize: 12 }}>{r.ignoredReason}</div> : null}
        </span>
      ),
    },
    ...(canEdit ? [{
      title: "", key: "act", width: 1,
      render: (_, r) => <Button size="small" disabled={busy} onClick={() => restore([r.ignoredId])}>Bring back</Button>,
    }] : []),
  ];

  return (
    <Card
      size="small" style={{ marginTop: 12 }}
      title={(
        <span>
          Ignored lines{" "}
          <span style={{ color: T.muted, fontWeight: 400 }}>
            {ignored.items} item{ignored.items === 1 ? "" : "s"} · {hrs(ignored.hours)}{withCost ? ` · ${money(ignored.cost)}` : ""} · not in any total or export above
          </span>
        </span>
      )}
      extra={(
        <span style={{ display: "flex", gap: 8 }}>
          {canEdit && open ? (
            <Popconfirm
              title={`Bring ${new Set(picked.map((k) => k.split(":")[0])).size} item(s) back into the report?`}
              disabled={!picked.length} onConfirm={() => restore(picked.map((k) => k.split(":")[0]))}
            >
              <Button size="small" type="primary" disabled={!picked.length || busy}>Bring back selected</Button>
            </Popconfirm>
          ) : null}
          <Tooltip title={open ? "" : "List them"}>
            <Button size="small" onClick={() => setOpen(!open)}>{open ? "Hide" : "Show"}</Button>
          </Tooltip>
        </span>
      )}
    >
      {open ? (
        <Table
          size="small" rowKey="_k" dataSource={rows} columns={columns}
          pagination={rows.length > 100 ? { pageSize: 100, showSizeChanger: false } : false}
          rowSelection={canEdit ? {
            selectedRowKeys: picked,
            // Lines of one item share its ignore: ticking or unticking one
            // does the same to the others.
            onSelect: (r, on) => setPicked((prev) => {
              const others = prev.filter((k) => k.split(":")[0] !== r.ignoredId);
              return on ? [...others, ...rows.filter((x) => x.ignoredId === r.ignoredId).map((x) => x._k)] : others;
            }),
            onSelectAll: (on) => setPicked(on ? rows.map((r) => r._k) : []),
          } : undefined}
        />
      ) : (
        <div style={{ color: T.muted, fontSize: 12.5 }}>
          These lines were left out on purpose. Show them to see who left each one out and why, or to bring them back.
        </div>
      )}
    </Card>
  );
}
